import React, { useState } from "react";
import { ShoppingBag, Trash2, Plus, Minus, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import CheckoutModal from "./CheckoutModal";

export default function CartDrawer() {
  const { t, S } = useLang();
  const { items, isOpen, setIsOpen, updateQty, removeItem, total } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col p-0 gap-0">
          <SheetHeader className="px-6 py-5 border-b border-border">
            <SheetTitle className="flex items-center gap-2 font-display text-xl">
              <ShoppingBag className="w-5 h-5 text-primary" />
              {t(S.cart.title)}
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
              <span className="grid place-items-center w-20 h-20 rounded-full bg-secondary text-primary mb-5">
                <ShoppingBag className="w-9 h-9" />
              </span>
              <p className="font-display font-bold text-lg text-foreground">{t(S.cart.empty)}</p>
              <p className="mt-2 text-sm text-muted-foreground">{t(S.cart.emptySub)}</p>
              <Button
                onClick={() => setIsOpen(false)}
                variant="outline"
                className="mt-6 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                {t(S.cart.continue)}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                {items.map((it) => (
                  <div key={it.id} className="flex gap-3 rounded-xl border border-border p-3">
                    <img src={it.img} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-accent">
                          {it.type === "transfer" ? t(S.cart.transfer) : t(S.cart.experience)}
                        </span>
                        <button onClick={() => removeItem(it.id)} className="text-muted-foreground hover:text-destructive shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <h4 className="mt-0.5 text-sm font-semibold text-foreground leading-snug line-clamp-2">{t(it.name)}</h4>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center border border-border rounded-full">
                          <button onClick={() => updateQty(it.id, it.qty - 1)} className="p-1.5 hover:text-primary" aria-label="minus">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="w-7 text-center text-sm font-semibold">{it.qty}</span>
                          <button onClick={() => updateQty(it.id, it.qty + 1)} className="p-1.5 hover:text-primary" aria-label="plus">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className="font-display font-bold text-primary">${it.price * it.qty} <span className="text-xs font-sans text-muted-foreground">USD</span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border px-6 py-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t(S.cart.total)}</span>
                  <span className="font-display font-extrabold text-2xl text-primary">${total} <span className="text-sm font-sans text-muted-foreground">USD</span></span>
                </div>
                <Button
                  onClick={() => setCheckoutOpen(true)}
                  size="lg"
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full h-12"
                >
                  {t(S.cart.checkout)}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </>
  );
}
